import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Handle, Position } from 'reactflow';
import { useTheme } from '../../../contexts/ThemeContext';
import * as toolService from '../../../services/toolService';
import { getHandleById } from '../workflowDefinitions';
import { WorkflowNodeType } from '../types';

const ToolNode = ({ data, selected = false }) => {
  const { darkMode } = useTheme();
  const [isSelectingTool, setIsSelectingTool] = useState(false);
  const [availableTools, setAvailableTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // CRITICAL: Store callback in ref to survive re-renders
  const onUpdateRef = useRef(data.onUpdate);

  // CRITICAL: Store tool data in ref to survive re-renders
  const toolDataRef = useRef({
    toolId: data.toolId,
    toolName: data.toolName,
    toolCategory: data.toolCategory,
    toolDescription: data.toolDescription,
    toolVersion: data.toolVersion
  });

  // Add logging every render
  console.log('🔧 TOOL-NODE-RENDER: Component rendered', {
    id: data.id,
    hasDataOnUpdate: !!data.onUpdate,
    hasRefCallback: !!onUpdateRef.current,
    isSelectingTool,
    dataKeys: Object.keys(data),
    refToolId: toolDataRef.current.toolId,
    propsToolId: data.toolId
  });

  // Update callback ref when data changes but preserve it if it becomes undefined
  useEffect(() => {
    console.log('🔧 TOOL-CALLBACK-EFFECT: useEffect triggered', {
      hasDataOnUpdate: !!data.onUpdate,
      hasRefCallback: !!onUpdateRef.current,
      onUpdateType: typeof data.onUpdate
    });
    
    if (data.onUpdate && typeof data.onUpdate === 'function') {
      onUpdateRef.current = data.onUpdate;
      console.log('🔧 TOOL-CALLBACK-PRESERVED: Stored callback in ref');
    } else if (!onUpdateRef.current) {
      console.warn('🔧 TOOL-CALLBACK-MISSING: data.onUpdate is not available and no ref callback exists!', {
        dataOnUpdate: data.onUpdate,
        typeOfDataOnUpdate: typeof data.onUpdate,
        fullData: data
      });
    } else {
      console.log('🔧 TOOL-CALLBACK-PRESERVING: data.onUpdate is undefined but ref callback exists, preserving ref');
    }
  }, [data.onUpdate]);

  // Update tool data ref when we get new data from props
  useEffect(() => {
    if (data.toolId) {
      console.log('🔧 TOOL-DATA-UPDATE: Updating tool data ref from props', {
        oldToolId: toolDataRef.current.toolId,
        newToolId: data.toolId
      });
      toolDataRef.current = {
        toolId: data.toolId,
        toolName: data.toolName,
        toolCategory: data.toolCategory,
        toolDescription: data.toolDescription,
        toolVersion: data.toolVersion
      };
    }
  }, [data.toolId, data.toolName, data.toolCategory, data.toolDescription, data.toolVersion]);

  // Get the handle definition for tool output
  const handleDef = getHandleById(WorkflowNodeType.TOOL, 'tool_output');

  // Fetch available tools when selecting a tool
  useEffect(() => {
    const fetchTools = async () => {
      try {
        setLoading(true);
        setError(null);
        // Use the existing API endpoint to fetch real tools
        const toolsData = await toolService.getTools();
        setAvailableTools(toolsData);
      } catch (err) {
        console.error('Error fetching tools:', err);
        setError('Failed to load tools');
      } finally {
        setLoading(false);
      }
    };

    if (isSelectingTool) {
      fetchTools();
    }
  }, [isSelectingTool]);

  // Filter tools based on search term
  const filteredTools = useMemo(() => {
    if (!searchTerm.trim()) return availableTools;
    
    return availableTools.filter(tool => 
      tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.metadata.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tool.metadata.tags && tool.metadata.tags.some(tag => 
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      ))
    );
  }, [availableTools, searchTerm]);

  // Handle tool selection
  const handleToolSelect = (toolId) => {
    console.log('🔧 TOOL-SELECT-START: About to select tool:', toolId);
    console.log('🔧 TOOL-SELECT-START: Current data.onUpdate:', !!data.onUpdate);
    console.log('🔧 TOOL-SELECT-START: Ref callback available:', !!onUpdateRef.current);
    
    const selectedTool = availableTools.find(tool => tool.name === toolId);
    
    // Update the tool data ref immediately to prevent flashing
    const newToolData = {
      toolId: selectedTool.name,
      toolName: selectedTool.metadata.name,
      toolCategory: selectedTool.metadata.category,
      toolDescription: selectedTool.metadata.documentation.summary,
      toolVersion: selectedTool.metadata.version ? 
        `${selectedTool.metadata.version.major}.${selectedTool.metadata.version.minor}.${selectedTool.metadata.version.patch}` : 
        '1.0.0'
    };
    
    console.log('🔧 TOOL-SELECT-UPDATE-REF: Updating tool data ref immediately', newToolData);
    toolDataRef.current = newToolData;
    
    // Always prioritize ref callback since it's more stable
    const callback = onUpdateRef.current || data.onUpdate;
    
    if (callback && typeof callback === 'function' && selectedTool) {
      console.log('🔧 TOOL-SELECT-CALLING: Calling callback (from ref or data)');
      callback(data.id, newToolData);
    } else {
      console.error('🔧 TOOL-SELECT-ERROR: No callback available or no tool selected!');
      console.error('🔧 TOOL-SELECT-ERROR: data.onUpdate:', data.onUpdate);
      console.error('🔧 TOOL-SELECT-ERROR: onUpdateRef.current:', onUpdateRef.current);
      console.error('🔧 TOOL-SELECT-ERROR: selectedTool:', selectedTool);
      
      // Try to recover using global debug callback
      if (window.workflowDebugCallback && selectedTool) {
        console.log('🔧 TOOL-SELECT-RECOVERY: Using global debug callback');
        window.workflowDebugCallback('update_tool', data.id, newToolData);
      } else {
        console.error('🔧 TOOL-SELECT-RECOVERY: No recovery mechanism available');
      }
    }
    
    setIsSelectingTool(false);
  };

  return (
    <div className={`
      ${darkMode ? 'bg-gray-700 text-gray-100' : 'bg-white'} 
      border-2 
      ${
        selected 
          ? 'border-green-500 bg-green-50 dark:bg-green-900/20 shadow-lg shadow-green-500/30 ring-2 ring-green-300 dark:ring-green-600' 
          : darkMode ? 'border-blue-600' : 'border-blue-300'
      } 
      rounded-lg p-3 shadow-md min-w-[180px] relative
      transition-all duration-200
      hover:shadow-lg hover:-translate-y-1
    `}>
      {/* Selection indicator badge */}
      {selected && (
        <div className="absolute -top-2 -right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-lg z-10">
          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
      )}
      {/* Position the handle on the right side - always connectable regardless of tool selection */}
      <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2">
        <Handle
          type={handleDef.type as any}
          position={Position.Right}
          id={handleDef.id}
          style={{ background: '#555', width: '8px', height: '8px' }}
          isConnectable={true}
        />
      </div>
      
      <div className="font-medium">
        {(toolDataRef.current.toolId || data.toolId) ? (
          <div className="flex items-center justify-between">
            <span>{toolDataRef.current.toolName || data.toolName || toolDataRef.current.toolId || data.toolId}</span>
            <button
              className={`ml-2 w-5 h-5 flex items-center justify-center rounded-full ${darkMode ? 'hover:bg-gray-600 text-gray-300' : 'hover:bg-gray-200 text-gray-600'}`}
              onClick={() => setIsSelectingTool(true)}
              title="Change tool"
            >
              ↺
            </button>
          </div>
        ) : (
          <button
            className={`w-full px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded`}
            onClick={() => setIsSelectingTool(true)}
          >
            Select Tool
          </button>
        )}
      </div>
      
      {(toolDataRef.current.toolId || data.toolId) && (
        <>
          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
            Category: {toolDataRef.current.toolCategory || data.toolCategory}
          </div>
          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
            {toolDataRef.current.toolDescription || data.toolDescription}
          </div>
          {(toolDataRef.current.toolVersion || data.toolVersion) && (
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
              Version: {toolDataRef.current.toolVersion || data.toolVersion}
            </div>
          )}
        </>
      )}
      
      {isSelectingTool && (
        <div className={`absolute z-10 mt-2 w-64 rounded-md shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} ring-1 ring-black ring-opacity-5`}>
          <div className={`p-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'} border-b`}>
            <input
              type="text"
              placeholder="Search tools..."
              className={`w-full p-1 text-sm rounded ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="max-h-60 overflow-y-auto p-1">
            {loading ? (
              <div className="p-2 text-center">Loading tools...</div>
            ) : error ? (
              <div className="p-2 text-center text-red-500">{error}</div>
            ) : filteredTools.length === 0 ? (
              <div className="p-2 text-center">No tools found</div>
            ) : (
              filteredTools.map(tool => (
                <div
                  key={tool.name}
                  className={`p-2 cursor-pointer ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                  onClick={() => handleToolSelect(tool.name)}
                >
                  <div className="font-medium">{tool.metadata.name}</div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {tool.metadata.documentation.summary}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ToolNode;
